import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ApiService} from '../../services/api.service';
import {ToastrService} from 'ngx-toastr';
import {AuthService} from '../..//services/auth.service';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {validateAllFormFields} from '../../../common/utils';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faCheck, faEye, faEyeSlash, faSquareCheck, faSquareXmark, faXmark} from '@fortawesome/free-solid-svg-icons';
import {LoadingService} from '../../services/loading.service';
import {RoleService} from '../../services/role.service';
import {VEHICLE_MONITORING_TYPES} from '../../shared/vehicle-monitoring/vehicle-monitoring.constants';

type UserNotificationPreferenceRow = {
    user_id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
    is_enabled: boolean;
    is_eligible: boolean;
    receive_all_monitoring_types: boolean;
    channels: { email: boolean; whatsapp: boolean };
    alert_types: { BLOCKLIST: boolean; HOTLIST: boolean; VEH_ALERT: boolean };
    monitoring_types: string[];
};

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.css'],
    imports: [CommonModule, ReactiveFormsModule, FormsModule, FontAwesomeModule]
})
export class SettingsComponent implements OnInit {
  alertDirectionForm!: FormGroup;
  changePasswordForm!: FormGroup;
  blocklistAlertDirection: string = 'ENTRY';
  hotlistAlertDirection: string = 'ENTRY';
  vehicleAlertDirection: string = 'ENTRY';
    rtoAreaDirection: string = 'ENTRY';
  parkingCapacity: number = 0;
  isPasswordVisible: boolean = false;
  passwordsMatch: boolean = false;
  isPassword: boolean = false;
  isAdmin: boolean = false;
  isSuperUser: boolean = false;
    canAccessAlertSettings: boolean = false;
    activeTab: string = 'password';

  constructor(private router: Router,
                private apiService: ApiService,
                private toastr: ToastrService,
                private authService: AuthService,
                private fb: FormBuilder,
                private route: ActivatedRoute,
                private loadingService: LoadingService,
              private roleService: RoleService,
                library: FaIconLibrary
              ){
    library.addIcons(faSquareCheck, faSquareXmark, faEye, faEyeSlash, faCheck, faXmark);
    

    this.changePasswordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', Validators.required],
      confirmNewpassword: ['', Validators.required]
    });

    // Add password match validation
    this.changePasswordForm.valueChanges.subscribe(() => {
      const newPass = this.changePasswordForm.get('newPassword')?.value;
      const confirmPass = this.changePasswordForm.get('confirmNewpassword')?.value;
      this.isPassword = newPass && confirmPass;
      this.passwordsMatch = newPass && confirmPass && newPass === confirmPass;
    });
  }

  ngOnInit() {
      const userInfo = this.authService.getCurrentUserInfo();
      const groups = Array.isArray(userInfo?.role) ? userInfo.role : [userInfo?.role].filter(Boolean);
      this.canAccessAlertSettings = this.roleService.canAccessAlertSettings(groups);
      this.activeTab = this.canAccessAlertSettings ? 'custom' : 'password';
      this.isAdmin = groups.includes('Admin');
      this.isSuperUser = this.authService.isSuperUser();

    this.alertDirectionForm = this.fb.group({
      blocklistAlertDirection: new FormControl({value: 'ENTRY', disabled: !this.isAdmin}, Validators.required),
      hotlistAlertDirection: new FormControl({value: 'ENTRY', disabled: !this.isAdmin}, Validators.required),
      vehicleAlertDirection: new FormControl({value: 'ENTRY', disabled: !this.isAdmin}, Validators.required),
        rtoAreaDirection: new FormControl({value: 'ENTRY', disabled: !this.isAdmin}, Validators.required),
      parkingCapacity: new FormControl({value: 0, disabled: !this.isAdmin}, [Validators.required, Validators.min(0)])
    });
      if (this.canAccessAlertSettings) {
          this.loadAlertDirections();
          this.loadNotificationPreferences();
    }
  }

  loadAlertDirections(): void {
    this.apiService.getAlertDirections().subscribe({
      next: (res: any) => {
        const blocklistMode = res.blocklist_alert_direction_mode || 'ENTRY';
        const hotlistMode = res.hotlist_alert_direction_mode || 'ENTRY';
        const vehicleAlertMode = res.vehicle_alert_direction_mode || 'ENTRY';
          const rtoMode = res.rto_area_direction_mode || 'ENTRY';
        const parkingCapacity = res.parking_capacity || 0;
        
        this.blocklistAlertDirection = blocklistMode;
        this.hotlistAlertDirection = hotlistMode;
        this.vehicleAlertDirection = vehicleAlertMode;
        this.rtoAreaDirection = rtoMode;
        this.parkingCapacity = parkingCapacity;
        
        this.alertDirectionForm.patchValue({
          blocklistAlertDirection: blocklistMode,
          hotlistAlertDirection: hotlistMode,
          vehicleAlertDirection: vehicleAlertMode,
          rtoAreaDirection: rtoMode,
          parkingCapacity
        });
      },
      error: (err) => {
        console.error('Error fetching alert directions', err);
        if (err.status !== 403 && err.status !== 0) {
          this.toastr.error("Failed to load alert directions", 'Error');
        }
      }
    });
  }              
  organizationLogoUrl: string | null = null;

  // New: notification channels state
  notifications = [
    { id: 'whatsapp', label: 'WhatsApp', enabled: true, comingSoon: false },
    { id: 'sms', label: '🔒 SMS (Coming Soon)', enabled: false, comingSoon: true },
    { id: 'email', label: 'EMAIL', enabled: true, comingSoon: false }
  ];
    userNotificationPreferences: UserNotificationPreferenceRow[] = [];
    monitoringTypeOptions = VEHICLE_MONITORING_TYPES;
    savingNotificationPreferences = false;
    loadingNotificationPreferences = false;
    expandedPreferenceUserId: number | null = null;
  setTab(tab: string) {
      if (tab === 'custom' && !this.canAccessAlertSettings) {
          return;
      }
    this.activeTab = tab;
  }

  onLogoUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.organizationLogoUrl = e.target.result;
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  onChangePassword() {
    if (this.changePasswordForm.valid) {
      this.loadingService.setLoading('change-password', true);
      let formdata = new FormData();
      formdata.append('old_password', this.changePasswordForm.value.currentPassword);
      formdata.append('new_password', this.changePasswordForm.value.newPassword);
      formdata.append('confirm_password', this.changePasswordForm.value.confirmNewpassword);
      this.apiService.changePassword(formdata).subscribe({
          next: (response: any) => {
            this.toastr.success("Password Changed successfully.", 'Success');
            this.changePasswordForm.reset();
            this.isPasswordVisible = false;
            this.passwordsMatch = false;
            this.isPassword = false;
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");
            localStorage.removeItem('activeLink');
            this.loadingService.setLoading('change-password', false);
            this.router.navigate(['/login']);
          },
          error: (err) => {
            this.loadingService.setLoading('change-password', false);
            if (err['error']['non_field_errors']) {
              err['error']['non_field_errors'].forEach((element: any) => {
                this.toastr.error(element, 'Error');
              });
            } else if (err['error']['old_password']) {
              err['error']['old_password'].forEach((element: any) => {
                this.toastr.error(`Current Password: ${element}`, 'Error');
              });
            } else if (err['error']['new_password']) {
              err['error']['new_password'].forEach((element: any) => {
                this.toastr.error(`New Password: ${element}`, 'Error');
              });
            } else if (err['error']['confirm_password']) {
              err['error']['confirm_password'].forEach((element: any) => {
                this.toastr.error(`Confirm Password: ${element}`, 'Error');
              });
            } else if (err['status'] !== 403 && err.status !== 0) {
              this.toastr.error("Something went wrong!", 'Error');
            }
          }
        }
      );
    } else {
      this.validateAllFormFields(this.changePasswordForm);
    }
  }

  get currentPassword() {
    return this.changePasswordForm.get('currentPassword');
  }

  get newPassword() {
    return this.changePasswordForm.get('newPassword');
  }

  get confirmNewpassword() {
    return this.changePasswordForm.get('confirmNewpassword');
  }

  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  validateAllFormFields(formGroup: FormGroup) {   
    Object.keys(formGroup.controls).forEach(field => {  
      const control = formGroup.get(field);             
      if (control instanceof FormGroup) {        
        this.validateAllFormFields(control);            
      } else {                                      
        control?.markAsTouched({ onlySelf: true });
      }
    });
  }

  // Toggle a notification channel on/off
  toggleNotification(id: string) {
    const item = this.notifications.find(n => n.id === id);
    if (!item || item.comingSoon) {
      return;
    }
    item.enabled = !item.enabled;
  }

  // Save alert directions
  saveAlertDirections() {
    if (this.alertDirectionForm.valid){
      const formValues = this.alertDirectionForm.value;
      const payload = {
        blocklist_alert_direction_mode: formValues.blocklistAlertDirection,
        hotlist_alert_direction_mode: formValues.hotlistAlertDirection,
        vehicle_alert_direction_mode: formValues.vehicleAlertDirection,
        rto_area_direction_mode: formValues.rtoAreaDirection,
        parking_capacity: formValues.parkingCapacity
      };

      this.apiService.setAlertDirections(payload).subscribe({
        next: (res: any) => {
          // Update component properties
          this.blocklistAlertDirection = formValues.blocklistAlertDirection;
          this.hotlistAlertDirection = formValues.hotlistAlertDirection;
          this.vehicleAlertDirection = formValues.vehicleAlertDirection;
          this.rtoAreaDirection = formValues.rtoAreaDirection;
          this.parkingCapacity = formValues.parkingCapacity;
          
          this.toastr.success("Alert directions saved successfully!", 'Success');
        },
        error: (err) => {
          console.error('Error saving alert directions:', err);
          if (err?.error?.non_field_errors) {
            err.error.non_field_errors.forEach((element: any) => {
              this.toastr.error(element, 'Error');
            });
          } else if (err?.error?.blocklist_alert_direction_mode) {
            err.error.blocklist_alert_direction_mode.forEach((element: any) => {
              this.toastr.error(element, 'Error');
            });
          } else if (err?.error?.hotlist_alert_direction_mode) {
            err.error.hotlist_alert_direction_mode.forEach((element: any) => {
              this.toastr.error(element, 'Error');
            });
          } else if (err?.error?.vehicle_alert_direction_mode) {
            err.error.vehicle_alert_direction_mode.forEach((element: any) => {
              this.toastr.error(element, 'Error');
            });
          } else if (err?.error?.rto_area_direction_mode) {
              err.error.rto_area_direction_mode.forEach((element: any) => {
                  this.toastr.error(element, 'Error');
              });
          } else if (err.status !== 403 && err.status !== 0) {
            this.toastr.error("Failed to save alert directions", 'Error');
          }
        }
      });
    } else {
      validateAllFormFields(this.alertDirectionForm);
    }
  }


  saveNotificationSettings() {
      this.saveNotificationPreferences();
  }

    loadNotificationPreferences(): void {
        if (!this.isAdmin) {
            return;
        }
        this.loadingNotificationPreferences = true;
        this.apiService.getNotificationPreferences().subscribe({
            next: (res: any) => {
                this.loadingNotificationPreferences = false;
                this.userNotificationPreferences = (res?.users || []).map((row: any) => this.normalizePreferenceRow(row));
            },
            error: (err) => {
                this.loadingNotificationPreferences = false;
                if (err.status !== 403 && err.status !== 0) {
                    this.toastr.error('Failed to load notification preferences', 'Error');
                }
            },
        });
    }

    private normalizePreferenceRow(row: any): UserNotificationPreferenceRow {
        const monitoringTypes = Array.isArray(row.monitoring_types) ? [...row.monitoring_types] : [];

        return {
            user_id: row.user_id,
            username: row.username,
            first_name: row.first_name || '',
            last_name: row.last_name || '',
            email: row.email || '',
            role: row.role || '',
            is_enabled: row.is_enabled !== false,
            is_eligible: row.is_eligible !== false,
            receive_all_monitoring_types: row.receive_all_monitoring_types === true,
            channels: {
                email: row.channels?.email !== false,
                whatsapp: row.channels?.whatsapp !== false,
            },
            alert_types: {
                BLOCKLIST: row.alert_types?.BLOCKLIST !== false,
                HOTLIST: row.alert_types?.HOTLIST !== false,
                VEH_ALERT: row.alert_types?.VEH_ALERT !== false,
            },
            monitoring_types: monitoringTypes,
        };
    }

    toggleUserPreferenceEnabled(row: UserNotificationPreferenceRow): void {
        row.is_enabled = !row.is_enabled;
    }

    toggleUserChannel(row: UserNotificationPreferenceRow, channel: 'email' | 'whatsapp'): void {
        row.channels[channel] = !row.channels[channel];
    }

    isMonitoringTypeSelected(row: UserNotificationPreferenceRow, code: string): boolean {
        return row.monitoring_types.includes(code);
    }

    toggleMonitoringType(row: UserNotificationPreferenceRow, code: string): void {
        if (row.receive_all_monitoring_types) {
            return;
        }
        if (row.monitoring_types.includes(code)) {
            row.monitoring_types = row.monitoring_types.filter(item => item !== code);
            return;
        }
        row.monitoring_types = [...row.monitoring_types, code];
    }

    toggleReceiveAllMonitoringTypes(row: UserNotificationPreferenceRow): void {
        if (!this.isAdmin || !row.is_enabled) {
            return;
        }
        row.receive_all_monitoring_types = !row.receive_all_monitoring_types;
    }

    isMonitoringTypeSelectionDisabled(row: UserNotificationPreferenceRow): boolean {
        return !this.isAdmin || !row.is_enabled || row.receive_all_monitoring_types;
    }

    isPreferenceRowExpanded(row: UserNotificationPreferenceRow): boolean {
        return this.expandedPreferenceUserId === row.user_id;
    }

    togglePreferenceRowExpand(row: UserNotificationPreferenceRow): void {
        this.expandedPreferenceUserId = this.isPreferenceRowExpanded(row) ? null : row.user_id;
    }

    getMonitoringTypesSummary(row: UserNotificationPreferenceRow): string {
        if (row.receive_all_monitoring_types) {
            return 'All Monitoring Types';
        }

        const selectedCount = row.monitoring_types.length;
        if (selectedCount === 0) {
            return 'No Monitoring Types';
        }

        const orderedLabels = this.monitoringTypeOptions
            .filter(option => row.monitoring_types.includes(option.value))
            .map(option => option.label);

        if (selectedCount === 1) {
            return orderedLabels[0];
        }
        if (selectedCount === 2) {
            return `${orderedLabels[0]}, ${orderedLabels[1]}`;
        }
        return `${orderedLabels[0]}, ${orderedLabels[1]} (+${selectedCount - 2})`;
    }

    saveNotificationPreferences(): void {
        if (!this.isAdmin || this.savingNotificationPreferences) {
            return;
        }

        this.savingNotificationPreferences = true;
        const payload = {
            preferences: this.userNotificationPreferences.map(row => ({
                user_id: row.user_id,
                is_enabled: row.is_enabled,
                channels: row.channels,
                alert_types: row.alert_types,
                receive_all_monitoring_types: row.receive_all_monitoring_types,
                monitoring_types: [...row.monitoring_types],
            })),
        };

        this.apiService.updateNotificationPreferences(payload).subscribe({
            next: () => {
                this.savingNotificationPreferences = false;
                this.toastr.success('Notification preferences saved successfully!', 'Success');
                this.loadNotificationPreferences();
            },
            error: (err) => {
                this.savingNotificationPreferences = false;
                if (err?.error?.detail) {
                    this.toastr.error(err.error.detail, 'Error');
                } else if (err.status !== 403 && err.status !== 0) {
                    this.toastr.error('Failed to save notification preferences', 'Error');
                }
            },
        });
    }

    getUserDisplayName(row: UserNotificationPreferenceRow): string {
        const fullName = `${row.first_name} ${row.last_name}`.trim();
        return fullName || row.username;
    }

    getRoleLabel(row: UserNotificationPreferenceRow): string {
        if (row.role === 'Admin') {
            return 'Administrator';
        }
        if (row.role === 'Member') {
            return 'Member';
        }
        return row.role || '';
  }
}
